import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { type User as SupabaseUser } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface User {
  id: string;
  username: string;
  displayName: string;
  email: string;
  phone: string;
  avatarUrl: string;
  bannerUrl: string;
  bio: string;
  joinedAt: string;
  role: 'user' | 'business' | 'admin';
}

interface UpdateProfileData {
  displayName?: string;
  phone?: string;
  avatarUrl?: string;
  bannerUrl?: string;
  bio?: string;
}

interface UserContextType {
  user: User | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (
    name: string,
    email: string,
    password: string,
  ) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  updateProfile: (data: UpdateProfileData) => Promise<{ success: boolean; error?: string }>;
  updateUsername: (username: string) => Promise<{ success: boolean; error?: string }>;
  updateEmail: (newEmail: string, password: string) => Promise<{ success: boolean; error?: string }>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  uploadAvatar: (file: File) => Promise<{ success: boolean; url?: string; error?: string }>;
  uploadBanner: (file: File) => Promise<{ success: boolean; url?: string; error?: string }>;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const UserContext = createContext<UserContextType | null>(null);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildUser(data: Record<string, unknown>, fallback: SupabaseUser): User {
  const rawRole = data.role as string;
  const role: User['role'] =
    rawRole === 'business' || rawRole === 'admin' ? rawRole : 'user';
  return {
    id: data.id as string,
    username: (data.username as string) || fallback.email?.split("@")[0] || "kullanici",
    displayName:
      (data.display_name as string) ||
      (data.username as string) ||
      (fallback.user_metadata?.display_name as string) ||
      fallback.email?.split("@")[0] ||
      "Kullanıcı",
    email: (data.email as string) || fallback.email || "",
    phone: (data.phone as string) ?? "",
    avatarUrl: (data.avatar_url as string) ?? "",
    bannerUrl: (data.banner_url as string) ?? "",
    bio: (data.bio as string) ?? "",
    joinedAt: (data.joined_at as string) || fallback.created_at,
    role,
  };
}

function buildUserFromAuth(su: SupabaseUser): User {
  return {
    id: su.id,
    username:
      (su.user_metadata?.username as string) || su.email?.split("@")[0] || "kullanici",
    displayName:
      (su.user_metadata?.display_name as string) ||
      (su.user_metadata?.username as string) ||
      su.email?.split("@")[0] ||
      "Kullanıcı",
    email: su.email || "",
    phone: (su.user_metadata?.phone as string) ?? "",
    avatarUrl: "",
    bannerUrl: "",
    bio: "",
    joinedAt: su.created_at,
    role: 'user',
  };
}

async function fetchProfile(su: SupabaseUser): Promise<User> {
  console.debug("[Auth] fetchProfile start", su.id);
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, username, display_name, email, phone, avatar_url, banner_url, bio, joined_at, role")
      .eq("id", su.id)
      .maybeSingle();

    if (error) {
      console.warn("[Auth] profiles query error:", error.message);
      return buildUserFromAuth(su);
    }

    if (!data) {
      console.warn("[Auth] no profile row found, creating one...");
      const slug = su.email?.split("@")[0] || "kullanici";
      const { data: newProfile } = await supabase
        .from("profiles")
        .insert({
          id: su.id,
          username: slug,
          display_name:
            (su.user_metadata?.display_name as string) || slug,
          email: su.email || "",
          phone: "",
          bio: "",
        })
        .select()
        .maybeSingle();

      if (newProfile) {
        console.debug("[Auth] created profile on the fly");
        return buildUser(newProfile as Record<string, unknown>, su);
      }

      const { data: existing } = await supabase
        .from("profiles")
        .select("id, username, display_name, email, phone, avatar_url, banner_url, bio, joined_at, role")
        .eq("id", su.id)
        .maybeSingle();

      if (existing) return buildUser(existing as Record<string, unknown>, su);

      return buildUserFromAuth(su);
    }

    supabase
      .from("profiles")
      .update({ last_seen_at: new Date().toISOString() })
      .eq("id", su.id)
      .then(() => {})
      .catch(() => {});

    console.debug("[Auth] profile loaded:", (data as Record<string, unknown>).username);
    return buildUser(data as Record<string, unknown>, su);
  } catch (err) {
    console.error("[Auth] fetchProfile exception:", err);
    return buildUserFromAuth(su);
  }
}

function mapError(msg: string): string {
  const m = msg.toLowerCase();
  if (m.includes("invalid login credentials") || m.includes("invalid credentials"))
    return "E-posta veya şifre hatalı. Lütfen kontrol edin.";
  if (m.includes("email not confirmed"))
    return "E-postanız doğrulanmamış. Lütfen gelen kutunuzu kontrol edin.";
  if (m.includes("already registered") || m.includes("user already exists"))
    return "Bu e-posta zaten kayıtlı. Giriş yapmayı deneyin.";
  if (m.includes("password should be at least") || m.includes("at least 6"))
    return "Şifre en az 6 karakter olmalıdır.";
  if (m.includes("invalid email") || m.includes("unable to validate email"))
    return "Geçersiz e-posta adresi.";
  if (m.includes("rate limit") || m.includes("too many requests"))
    return "Çok fazla deneme yapıldı. Birkaç dakika bekleyin.";
  if (m.includes("network") || m.includes("fetch failed") || m.includes("failed to fetch"))
    return "Bağlantı hatası. İnternetinizi kontrol edin.";
  if (m.includes("signup is disabled"))
    return "Kayıt şu an kapalı. Daha sonra tekrar deneyin.";
  return "Bir hata oluştu. Lütfen tekrar deneyin.";
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser]       = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // ── Session restore (page reload / tab focus) ──────────────────────────────
  useEffect(() => {
    let alive = true;

    // Hard timeout: never stay loading >7s
    const guard = setTimeout(() => {
      if (alive) {
        console.warn("[Auth] loading guard triggered after 7s");
        setLoading(false);
      }
    }, 7000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!alive) return;

        console.debug("[Auth] event:", event, "session:", !!session);

        if (event === "INITIAL_SESSION") {
          if (session?.user) {
            const profile = await fetchProfile(session.user);
            if (alive) setUser(profile);
          }
          clearTimeout(guard);
          if (alive) setLoading(false);
          return;
        }

        if (event === "SIGNED_OUT") {
          if (alive) { setUser(null); setLoading(false); }
          return;
        }

        // SIGNED_IN: login() handles this directly via setUser().
        // We only need to handle the edge case where the event fires
        // WITHOUT login() having been called (e.g., magic link, OAuth).
        // Normal email/password login is handled inside login() itself.
        if (event === "SIGNED_IN" && session?.user) {
          // Only act if user is still null (means login() didn't set it yet)
          setUser((current) => {
            if (current !== null) return current; // login() already handled it
            // Will trigger a side-effect fetch below — but we can't do async here
            // so we return current and let the effect below handle it
            return current;
          });
          // If user is null after SIGNED_IN, fetch profile
          // We schedule it as a microtask to not block the event
          Promise.resolve().then(async () => {
            if (!alive) return;
            setUser((current) => {
              if (current !== null) return current; // already set by login()
              return current;
            });
            // Check actual current value via getSession
            const { data } = await supabase.auth.getSession();
            if (alive && data.session?.user) {
              setUser((current) => {
                if (current !== null) return current;
                return null; // will be set below
              });
              // Re-fetch profile for the logged-in user if still null
              const { data: sessionData } = await supabase.auth.getSession();
              if (alive && sessionData.session?.user) {
                const profile = await fetchProfile(sessionData.session.user);
                if (alive) {
                  setUser((current) => {
                    if (current !== null) return current; // login() already set it
                    return profile; // only set if login() didn't
                  });
                }
              }
            }
          });
        }
      },
    );

    return () => {
      alive = false;
      clearTimeout(guard);
      subscription.unsubscribe();
    };
  }, []);

  // ─── LOGIN ─────────────────────────────────────────────────────────────────
  const login = useCallback(async (email: string, password: string) => {
    const e = email.trim().toLowerCase();

    if (!e) return { success: false, error: "E-posta boş olamaz." };
    if (!password) return { success: false, error: "Şifre boş olamaz." };
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e))
      return { success: false, error: "Geçerli bir e-posta girin." };

    console.debug("[Auth] login attempt:", e);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: e,
        password,
      });

      if (error) {
        console.warn("[Auth] signInWithPassword error:", error.message);
        return { success: false, error: mapError(error.message) };
      }

      if (!data.user) {
        console.warn("[Auth] signInWithPassword returned no user");
        return { success: false, error: "Oturum oluşturulamadı. Lütfen tekrar deneyin." };
      }

      console.debug("[Auth] signInWithPassword success, fetching profile...");
      const profile = await fetchProfile(data.user);
      console.debug("[Auth] profile ready, setting user:", profile.username);
      setUser(profile);
      setLoading(false);
      return { success: true };
    } catch (err) {
      console.error("[Auth] login exception:", err);
      return { success: false, error: "Bağlantı hatası. İnternetinizi kontrol edin." };
    }
  }, []);

  // ─── REGISTER ──────────────────────────────────────────────────────────────
  const register = useCallback(async (name: string, email: string, password: string) => {
    const n = name.trim();
    const e = email.trim().toLowerCase();

    if (!n || n.length < 2) return { success: false, error: "Adınız en az 2 karakter olmalıdır." };
    if (n.length > 50) return { success: false, error: "Adınız en fazla 50 karakter olabilir." };
    if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e))
      return { success: false, error: "Geçerli bir e-posta girin." };
    if (!password || password.length < 6)
      return { success: false, error: "Şifre en az 6 karakter olmalıdır." };

    console.debug("[Auth] register attempt:", e);

    try {
      const { data, error } = await supabase.auth.signUp({
        email: e,
        password,
        options: { data: { display_name: n } },
      });

      if (error) return { success: false, error: mapError(error.message) };
      if (!data.user) return { success: false, error: "Kayıt oluşturulamadı." };

      // Fire-and-forget welcome email
      supabase.functions
        .invoke("send-welcome-email", { body: { email: e, username: n } })
        .catch(() => {});

      if (data.session) {
        // Email confirmation disabled → auto sign-in
        console.debug("[Auth] register auto-signin, waiting for trigger...");
        await new Promise((r) => setTimeout(r, 600)); // let DB trigger create profile
        const profile = await fetchProfile(data.user);
        console.debug("[Auth] register profile ready:", profile.username);
        setUser(profile);
        setLoading(false);
        return { success: true };
      }

      // Email confirmation required
      return {
        success: false,
        error:
          "Hesabınız oluşturuldu! E-postanıza doğrulama bağlantısı gönderildi. Onayladıktan sonra giriş yapabilirsiniz.",
      };
    } catch (err) {
      console.error("[Auth] register exception:", err);
      return { success: false, error: "Kayıt sırasında hata oluştu. Tekrar deneyin." };
    }
  }, []);

  // ─── LOGOUT ────────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    console.debug("[Auth] logout");
    setUser(null);
    await supabase.auth.signOut();
  }, []);

  // ─── UPDATE PROFILE ────────────────────────────────────────────────────────
  const updateProfile = useCallback(async (data: UpdateProfileData) => {
    if (!user) return { success: false, error: "Giriş yapılı değil." };
    try {
      const updates: Record<string, string> = {};
      if (data.displayName !== undefined) updates.display_name = data.displayName.trim();
      if (data.phone !== undefined) updates.phone = data.phone.trim().replace(/\D/g, "");
      if (data.avatarUrl !== undefined) updates.avatar_url = data.avatarUrl;
      if (data.bannerUrl !== undefined) updates.banner_url = data.bannerUrl;
      if (data.bio !== undefined) updates.bio = data.bio.trim();
      if (!Object.keys(updates).length) return { success: true };

      const { error } = await supabase.from("profiles").update(updates).eq("id", user.id);
      if (error) return { success: false, error: mapError(error.message) };

      setUser((prev) =>
        prev
          ? {
              ...prev,
              ...(data.displayName !== undefined && { displayName: data.displayName.trim() }),
              ...(data.phone !== undefined && { phone: data.phone.trim().replace(/\D/g, "") }),
              ...(data.avatarUrl !== undefined && { avatarUrl: data.avatarUrl }),
              ...(data.bannerUrl !== undefined && { bannerUrl: data.bannerUrl }),
              ...(data.bio !== undefined && { bio: data.bio.trim() }),
            }
          : null,
      );
      return { success: true };
    } catch {
      return { success: false, error: "Güncelleme sırasında hata oluştu." };
    }
  }, [user]);

  // ─── UPDATE USERNAME ───────────────────────────────────────────────────────
  const updateUsername = useCallback(async (newUsername: string) => {
    if (!user) return { success: false, error: "Giriş yapılı değil." };
    const slug = newUsername.trim().toLowerCase().replace(/[^a-z0-9_]/g, "");
    if (slug.length < 3) return { success: false, error: "Kullanıcı adı en az 3 karakter olmalıdır." };
    if (slug.length > 30) return { success: false, error: "Kullanıcı adı en fazla 30 karakter olabilir." };

    try {
      // Check uniqueness
      const { data: existing } = await supabase
        .from("profiles")
        .select("id")
        .eq("username", slug)
        .neq("id", user.id)
        .maybeSingle();

      if (existing) return { success: false, error: "Bu kullanıcı adı zaten alınmış." };

      const { error } = await supabase.from("profiles").update({ username: slug }).eq("id", user.id);
      if (error) return { success: false, error: mapError(error.message) };

      setUser((prev) => prev ? { ...prev, username: slug } : null);
      return { success: true };
    } catch {
      return { success: false, error: "Kullanıcı adı güncellenemedi." };
    }
  }, [user]);

  // ─── UPDATE EMAIL ──────────────────────────────────────────────────────────
  const updateEmail = useCallback(async (newEmail: string, password: string) => {
    if (!user) return { success: false, error: "Giriş yapılı değil." };
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim()))
      return { success: false, error: "Geçersiz e-posta." };
    try {
      const { error: re } = await supabase.auth.signInWithPassword({ email: user.email, password });
      if (re) return { success: false, error: "Mevcut şifreniz hatalı." };
      const { error } = await supabase.auth.updateUser({ email: newEmail.trim().toLowerCase() });
      if (error) return { success: false, error: mapError(error.message) };
      await supabase.from("profiles").update({ email: newEmail.trim().toLowerCase() }).eq("id", user.id);
      setUser((p) => p ? { ...p, email: newEmail.trim().toLowerCase() } : null);
      return { success: true };
    } catch {
      return { success: false, error: "E-posta güncellenemedi." };
    }
  }, [user]);

  // ─── UPDATE PASSWORD ───────────────────────────────────────────────────────
  const updatePassword = useCallback(async (current: string, next: string) => {
    if (!user) return { success: false, error: "Giriş yapılı değil." };
    if (next.length < 6) return { success: false, error: "Yeni şifre en az 6 karakter olmalı." };
    if (current === next) return { success: false, error: "Yeni şifre mevcut ile aynı olamaz." };
    try {
      const { error: re } = await supabase.auth.signInWithPassword({ email: user.email, password: current });
      if (re) return { success: false, error: "Mevcut şifreniz hatalı." };
      const { error } = await supabase.auth.updateUser({ password: next });
      if (error) return { success: false, error: mapError(error.message) };
      return { success: true };
    } catch {
      return { success: false, error: "Şifre güncellenemedi." };
    }
  }, [user]);

  // ─── UPLOAD AVATAR ─────────────────────────────────────────────────────────
  const uploadAvatar = useCallback(async (file: File) => {
    if (!user) return { success: false, error: "Giriş yapılı değil." };
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type))
      return { success: false, error: "Sadece JPG, PNG, WEBP veya GIF yüklenebilir." };
    if (file.size > 2 * 1024 * 1024)
      return { success: false, error: "Dosya 2MB\'dan büyük olamaz." };
    try {
      const ext = file.type.split("/")[1].replace("jpeg", "jpg");
      const filePath = `${user.id}/avatar.${ext}`;
      const { error: ue } = await supabase.storage
        .from("avatars")
        .upload(filePath, file, { upsert: true, contentType: file.type });
      if (ue) return { success: false, error: "Fotoğraf yüklenemedi: " + ue.message };
      const { data: ud } = supabase.storage.from("avatars").getPublicUrl(filePath);
      const url = `${ud.publicUrl}?v=${Date.now()}`;
      const res = await updateProfile({ avatarUrl: url });
      if (!res.success) return res;
      return { success: true, url };
    } catch {
      return { success: false, error: "Fotoğraf yüklenirken hata oluştu." };
    }
  }, [user, updateProfile]);

  // ─── UPLOAD BANNER ─────────────────────────────────────────────────────────
  const uploadBanner = useCallback(async (file: File) => {
    if (!user) return { success: false, error: "Giriş yapılı değil." };
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type))
      return { success: false, error: "Sadece JPG, PNG veya WEBP yüklenebilir." };
    if (file.size > 5 * 1024 * 1024)
      return { success: false, error: "Dosya 5MB\'dan büyük olamaz." };
    try {
      const ext = file.type.split("/")[1].replace("jpeg", "jpg");
      const filePath = `${user.id}/banner.${ext}`;
      const { error: ue } = await supabase.storage
        .from("avatars")
        .upload(filePath, file, { upsert: true, contentType: file.type });
      if (ue) return { success: false, error: "Banner yüklenemedi: " + ue.message };
      const { data: ud } = supabase.storage.from("avatars").getPublicUrl(filePath);
      const url = `${ud.publicUrl}?v=${Date.now()}`;
      const res = await updateProfile({ bannerUrl: url });
      if (!res.success) return res;
      return { success: true, url };
    } catch {
      return { success: false, error: "Banner yüklenirken hata oluştu." };
    }
  }, [user, updateProfile]);

  // ─── REFRESH PROFILE ───────────────────────────────────────────────────────
  const refreshProfile = useCallback(async () => {
    try {
      const { data } = await supabase.auth.getSession();
      if (data.session?.user) {
        const profile = await fetchProfile(data.session.user);
        setUser(profile);
      }
    } catch (err) {
      console.error("[Auth] refreshProfile exception:", err);
    }
  }, []);

  // ─── AUTO REFRESH ON WINDOW FOCUS ─────────────────────────────────────────
  // When user switches tabs/windows and comes back, re-fetch profile
  // This ensures role changes made by admin are picked up automatically
  useEffect(() => {
    const handleFocus = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (data.session?.user) {
          const profile = await fetchProfile(data.session.user);
          setUser(profile);
        }
      } catch {
        // silently fail
      }
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  return (
    <UserContext.Provider
      value={{ user, loading, refreshProfile, login, register, logout, updateProfile, updateUsername, updateEmail, updatePassword, uploadAvatar, uploadBanner }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser(): UserContextType {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be inside UserProvider");
  return ctx;
}
